import { AbstractDestroyable, Callback1, MutableMapStore, Store, StoreObserver } from '@deqo/foundation';
import { TopicData } from '@deqo/podopolis';
import { Sounds } from '../../../@resources/sounds';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { AudioManager } from '../../../platform/AudioManager';
import { resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { MyRoleInfo } from '../../canvas/MyRoleInfo';
import { Hangout } from '../../hangouts/Hangout';
import { HangoutClient } from '../../hangouts/HangoutClient';
import { HangoutTopic } from '../../hangouts/HangoutTopic';
import { optionsPopupWith } from '../../popups/OptionsPopup';
import { PopupManager } from '../../popups/PopupManager';
import { ToastHub } from '../toast/ToastHub';
import { WaitingRoomEntry } from './WaitingRoomEntry';

const noSlotsPopup = optionsPopupWith(
    'Guest Limit Reached',
    'Sorry, there are no more available guest slots for this board.  ' +
    'You can manage existing slots by clicking the board name in the top left and choosing Manage Access.',
    [
        [true, 'OK', 'primary', 'confirm'],
    ]
);

export class WaitingRoomManager extends AbstractDestroyable implements Store<Map<string, WaitingRoomEntry>> {

    @resolve(ApiClient) private readonly api!: ApiClient;
    @resolve(AudioManager) private readonly audio!: AudioManager;
    @resolve(Hangout) private readonly hangout!: Hangout;
    @resolve(PopupManager) private readonly popups!: PopupManager;
    @resolve(ProfileModel) private readonly me!: ProfileModel;
    @resolve(EditorSession) private readonly session!: EditorSession;
    @resolve(ToastHub) private readonly toast!: ToastHub;

    private store = new MutableMapStore<string, WaitingRoomEntry>();
    private topic = null as HangoutTopic;

    protected start = () => {
        this.syncSubscription();
        this.burden(
            this.session.observe(MyRoleInfo, this.syncSubscription),
            () => this.unsubscribe(),
        );
    };

    public destroy(): void {
        super.destroy();
    }

    public getState() {
        return this.store.getState();
    }

    public observe(observer: StoreObserver<Map<string, WaitingRoomEntry>>);
    public observe<P>(path: Callback1<P, Map<string, WaitingRoomEntry>>, observer: StoreObserver<P>);
    public observe(path: any, observer?: any) {
        return this.store.observe(path, observer);
    }

    public async respondTo(userId: string, response: 'allow' | 'refuse') {
        if (!this.store.has(userId)) return;
        if (!this.topic) return;

        const userInfo = this.store.get(userId);
        this.topic.patch({ [userId]: null });

        if (response === 'allow') {
            const board = this.session.get(BoardInfo);
            const { ok } = await this.api.exec(v2.boards.members.add(board.id, userId));
            if (!ok) {
                response = 'refuse';
                this.popups.invoke(noSlotsPopup);
            }
        }

        this.toast.send(`ptpt:${response}`, { targetName: userInfo.name });
        this.topic.broadcast('knockReply', { userId, response });
    }

    private onTopicKnock = (_sender: HangoutClient) => {
        this.audio.play(Sounds.Knock);
    };

    private onTopicDataChange = (data: TopicData) => {
        this.store.mutateTo(new Map(Object.entries(data)));
    };

    private syncSubscription = () => {
        const { canManage } = this.session.get(MyRoleInfo);

        if (!canManage && this.topic) {
            this.unsubscribe();
        }

        if (canManage && !this.topic) {
            this.subscribe();
        }
    };

    private async subscribe() {
        // Topic is board:{boardId}:waiting
        const board = this.session.get(BoardInfo);
        const topic = this.topic = this.hangout.subscribeTo(`board:${board.id}:waiting`);
        topic.receive('knock', this.onTopicKnock);
        topic.observe(this.onTopicDataChange);
        await topic.patch({ [this.me.id]: WaitingRoomEntry(this.me, 'keeping') });
    }

    private async unsubscribe() {
        if (this.topic) {
            // Remove ourselves from the topic
            const me = this.session.get(MyProfileInfo);
            await this.topic.patch({ [me.id]: null });
            this.topic.destroy();
            this.topic = null;
        }
    }
}
